import { useState } from "react";
import {
  getInviteToken,
  loginWithPassword,
  registerAccount,
  requestPasswordReset,
} from "../lib/api";

const detail = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ") || fallback;
  return fallback;
};

const field =
  "w-full border border-[var(--line-hi)] bg-[#0a0a0d] px-4 py-3 font-data text-[12px] text-zinc-100 outline-none placeholder:text-zinc-700";

/** Email and password, alongside Google. Both still need an operator's invitation. */
export const CredentialForm = ({ onDone }) => {
  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [invite, setInvite] = useState(getInviteToken() || "");
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    setNote("");
    try {
      if (mode === "forgot") {
        const r = await requestPasswordReset(email.trim());
        setNote(r.note);
      } else if (mode === "register") {
        await registerAccount({
          email: email.trim(),
          password,
          name: name.trim() || null,
          invite: invite.trim() || null,
        });
        onDone();
      } else {
        await loginWithPassword({ email: email.trim(), password });
        onDone();
      }
    } catch (e2) {
      setErr(detail(e2, "That did not work."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="credential-form" className="mt-7 space-y-3">
      <input
        data-testid="credential-email-input"
        type="email"
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@company.com"
        className={field}
      />

      {mode === "register" && (
        <input
          data-testid="credential-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="your name"
          className={field}
        />
      )}

      {mode !== "forgot" && (
        <input
          data-testid="credential-password-input"
          type="password"
          autoComplete={mode === "register" ? "new-password" : "current-password"}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={mode === "register" ? "at least 10 characters" : "password"}
          className={field}
        />
      )}

      {mode === "register" && (
        <>
          <input
            data-testid="credential-invite-input"
            value={invite}
            onChange={(e) => setInvite(e.target.value)}
            placeholder="invitation code"
            className={`${field} tracking-[0.25em] uppercase`}
          />
          <p className="font-data text-[10px] leading-relaxed text-zinc-600">
            No code means no account. Whoever invited you has one, or sent you a link that carries
            it.
          </p>
        </>
      )}

      <button
        type="submit"
        data-testid="credential-submit-btn"
        disabled={busy || !email.trim()}
        className="btn-stark w-full justify-center !py-3 disabled:opacity-40"
      >
        {busy
          ? "Working"
          : mode === "register"
            ? "Open my engagement"
            : mode === "forgot"
              ? "Send a reset link"
              : "Sign in"}
      </button>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-1">
        {mode !== "register" && (
          <button
            type="button"
            data-testid="credential-mode-register"
            onClick={() => {
              setMode("register");
              setErr("");
            }}
            className="label hover:text-zinc-200"
          >
            I have an invitation →
          </button>
        )}
        {mode !== "signin" && (
          <button
            type="button"
            data-testid="credential-mode-signin"
            onClick={() => {
              setMode("signin");
              setErr("");
            }}
            className="label hover:text-zinc-200"
          >
            ← Sign in instead
          </button>
        )}
        {mode === "signin" && (
          <button
            type="button"
            data-testid="credential-mode-forgot"
            onClick={() => {
              setMode("forgot");
              setErr("");
            }}
            className="label hover:text-zinc-200"
          >
            Forgotten password
          </button>
        )}
      </div>

      {err && (
        <p data-testid="credential-error" className="font-data text-[11px] leading-relaxed text-zinc-300">
          — {err}
        </p>
      )}
      {note && (
        <p data-testid="credential-note" className="font-data text-[11px] leading-relaxed text-zinc-400">
          — {note}
        </p>
      )}
    </form>
  );
};

export default CredentialForm;
